import { useContext, useMemo, useState } from 'react';
import { t } from '@sm/intl';
import { StaticContext } from '@sm/webassets';

import { STRIPE_ELIGIBLE_PACKAGES } from '~pricing/lib/constants';
import { PackageIds, Packages } from '~shared/constants/pricing';
import { PricingPackage } from '~shared/types/pricing/pricingsvc';

import COPY from './copy';
import useTrackingDetails from './useTrackingDetails';
import { CtaArgs, CtaHook } from './types';
import { usePricingExperiments } from '~shared/hooks/usePricingExperiments';
import { EXPERIMENT_IDENTIFIERS } from '~server/routes/Pricing/experimentsConfig';
import { usePricingState } from '~shared/hooks/usePricingState';

let isUpgradeable: boolean;
let packageIsUserPackage: boolean;
let isAuthenticated: boolean;
let isStripeEligible: boolean;
let useStripeCheckout: boolean;
let numSeats;
let numSeatsQueryParam: string | null;

/**
 * if user is eligible for edu discount, get the appropriate URL
 * @param pageUtSource
 * @param pricingPackage
 * @param userPackage
 * @returns
 */
function getEduDiscountUrl(pageUtSource: string, pricingPackage: PricingPackage, userPackage: PricingPackage): string {
  let eduDiscountUrl = '';
  if (isAuthenticated && userPackage?.packageName === Packages.Basic) {
    eduDiscountUrl = `/billing/pw/discount/verify?package_id=${pricingPackage?.id}&ut_source=${pageUtSource}`;
  } else if (isAuthenticated) {
    eduDiscountUrl = `/upgrade?package_id=${pricingPackage?.id}&e=edu_faq&ut_source=${pageUtSource}`;
  } else {
    eduDiscountUrl = `/user/sign-up/?e=edu_faq&sm=${pricingPackage?.encryptedEduParam}&ut_source=${pageUtSource}`;
  }
  return eduDiscountUrl;
}

/**
 * if user is logged in and package can be upgrade, determine the URL
 * @param pricingPackage
 * @param pageUtSource
 * @param showEduDiscount
 * @param userPackage
 * @returns
 */
function getUrlForUpgrade(
  pricingPackage: PricingPackage,
  pageUtSource: string,
  showEduDiscount: boolean,
  userPackage: PricingPackage
): string {
  let newUrl = '';
  if (pricingPackage?.packageName === Packages.Enterprise) {
    const utSource2 = pageUtSource?.includes('details') ? 'teams_details' : 'teams';
    newUrl = `/mp/contact-sales/?ut_source=${pageUtSource}&ut_source2=${utSource2}&ut_source3=primary_contact_cta&package_id=${pricingPackage?.id}`;
  } else if (showEduDiscount) {
    newUrl = getEduDiscountUrl(pageUtSource, pricingPackage, userPackage);
  } else if (isAuthenticated) {
    if (userPackage?.packageName === Packages.Basic) {
      newUrl = `/billing/checkout?package_id=${pricingPackage?.id}&ut_source=${pageUtSource}${
        numSeatsQueryParam ?? ''
      }`;
    } else {
      newUrl = `/upgrade?package_id=${pricingPackage?.id}&ut_source=${pageUtSource}${numSeatsQueryParam ?? ''}`;
    }
  } else {
    newUrl = `/user/sign-up/?sm=${pricingPackage?.encryptedCtaParam}&ut_source=${pageUtSource}${
      numSeatsQueryParam ?? ''
    }`;
  }
  return newUrl;
}

/**
 * Determine new CTA URL from whether user is logged in, has a package that could be upgraded,
 * how many seats in their plan, and if we should show the edu discount and use Stripe checkout
 * @param args CtaArgs
 * @returns ctaUrl
 */
function getNewCtaUrl(args: CtaArgs): string {
  const { pageUtSource, showEduDiscount, pricingPackage, userPackage } = args;
  let newCtaUrl = '';
  if (packageIsUserPackage) {
    /* Since all users start with a basic package, we check if they are authenticated or not
     and set the correct sign up URL */
    if (!isAuthenticated) {
      newCtaUrl = `/user/sign-up/?ut_source=${pageUtSource}`;
    }
  } else if (isUpgradeable) {
    newCtaUrl = getUrlForUpgrade(pricingPackage, pageUtSource, showEduDiscount, userPackage);
  }

  /* if this is good to go for stripe checkout, make the URL swap here; it's a pretty easy find/replace scenario on the path.
   GROW-2426: if it's the user-subscribed package and using stripe do not set the url
   Setting the url renders a link, see https://code.corp.surveymonkey.com/wrench/wrench/blob/master/packages/button/Button.tsx */
  if (useStripeCheckout && !packageIsUserPackage && !showEduDiscount) {
    newCtaUrl = `/checkout${newCtaUrl.replace('/billing/checkout', '').replace('/upgrade', '')}`;
  }
  return newCtaUrl;
}

/**
 * Derive CTA label from whether the user is logged in, what package they're signed up for, and if they can upgrade it.
 * @param pricingPackage
 * @returns
 */
function getNewCtaLabel(pricingPackage: PricingPackage): string {
  let ctaLabel = '';
  if (packageIsUserPackage) {
    /* Since all users start with a basic package, we check if they are authenticated or not
       and set the correct cta label and sign up URL */
    if (!isAuthenticated) {
      ctaLabel = t(COPY.SIGN_UP);
    } else {
      ctaLabel = t(COPY.YOUR_PLAN);
    }
  } else if (isUpgradeable) {
    // Labels first; simpler logic
    if (pricingPackage?.packageName === Packages.Enterprise) {
      ctaLabel = t(COPY.CONTACT_US);
    } else {
      ctaLabel = t(isAuthenticated ? COPY.SELECT : COPY.SIGN_UP);
    }
  }
  return ctaLabel;
}

/**
 * Given a user's details & the package they've signed up for,
 * returns the relevant CTA label and URL for signup/upgrade
 * @param {PricingPackage} pricingPackage   Details of the pricing and features available in a package
 * @returns CtaHook
 */
export default function useCta(pricingPackage: PricingPackage): CtaHook {
  const { user } = useContext(StaticContext);
  const {
    state: {
      packages: { userPackage, showEduDiscount },
    },
  } = usePricingState();
  const [isEnabled, setIsEnabled] = useState(true);
  const [isUserPackage, setIsUserPackage] = useState(false);
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const { pageUtSource } = useTrackingDetails();
  const { priceExperiment } = usePricingExperiments();

  const isPnPRefresh =
    priceExperiment.hasAssignment && priceExperiment.treatmentName === EXPERIMENT_IDENTIFIERS.GROW_2792.treatment;
  const {
    state: {
      grow2792: { advantageUsers, premierUsers },
    },
  } = usePricingState();

  useMemo(() => {
    /* if we don't have the user's package details (because they're in a package that's
       not part of the standard pricingweb load-out), default to allowing the CTA
       TODO: at the server side, if the user's package is not returned in the pricingsvc
       call, we need to do a separate fetch to retrieve it so we can reflect this CTA correctly */
    isUpgradeable = userPackage?.details ? userPackage.details.tier < pricingPackage?.details.tier : true;
    packageIsUserPackage = userPackage?.packageName === pricingPackage?.packageName;
    isAuthenticated = user.isAuthenticated;
    isStripeEligible = user.isStripeEligible;
    useStripeCheckout = isStripeEligible && STRIPE_ELIGIBLE_PACKAGES.includes(pricingPackage?.packageName);

    numSeats = Number(pricingPackage.id) === PackageIds.TEAM_ADVANTAGE_ANNUAL ? advantageUsers : premierUsers;
    numSeatsQueryParam =
      isPnPRefresh &&
      pricingPackage &&
      [PackageIds.TEAM_ADVANTAGE_ANNUAL, PackageIds.TEAM_PREMIER_ANNUAL].includes(Number(pricingPackage.id))
        ? `&num_seats=${numSeats}`
        : null;

    const newCtaLabel = getNewCtaLabel(pricingPackage);
    setCtaLabel(newCtaLabel);

    const args = {
      pageUtSource,
      showEduDiscount,
      pricingPackage,
      userPackage,
    };
    const newCtaUrl = getNewCtaUrl(args);
    setCtaUrl(newCtaUrl);

    setIsEnabled(!isAuthenticated || isUpgradeable);
    setIsUserPackage(packageIsUserPackage);
  }, [user, userPackage, pricingPackage, isPnPRefresh, advantageUsers, premierUsers, showEduDiscount, pageUtSource]);

  return {
    isEnabled,
    isUserPackage,
    ctaUrl,
    ctaLabel,
  };
}
